import { useEffect, useState } from "react";
import { Input, Table } from "reactstrap";
import useCheckGroup from "../../../hook/useCheckGroup";
import useToggle from "../../../hook/useToggle";
import { IDigitalAddress } from "../../../types/contact";
import { AddIcon, CheckIcon, CheckMarkIcon, DeleteIcon, EditIcon } from "../../common/icons";
import { DELETE_INDIVIDUAL_EMAILS, GET_INDIVIDUAL_EMAILS, SET_INDIVIDUAL_PRIMARY_EMAIL } from "../../../graphql/contact-gql";
import { useLazyQuery, useMutation } from "@apollo/client";
import { notifySuccess } from "../../../services/notification";
import ImgButton from "../../common/img-button/img-button";
import IndividualDigitalAddressUpdate from "./individual-digital-address-update";
import confirm from "../../common/confirm";
import IndividualDigitalAddressCreate from "./individual-digital-address-create";
import SpinnerOverlay from "../../common/spinner/spinner";
import useAuth from "../../../hook/useAuth";
import Button from "../../common/button";


type IndividualDigitalAddressProps = {
    individualId: number;
    orgReadOnly:boolean;
};

const IndividualDigitalAddress = (props: IndividualDigitalAddressProps ) => {
    const {individualId,orgReadOnly} = props
    const [digitalAddress, setDigitalAddress] = useState<IDigitalAddress>()
    const [groupCheckShow, setGroupCheckShow] = useState<boolean>()
    const {isOpen: editDigitalAddressOpen, toggle: toggleEditDigitalAddress} = useToggle()
    const {isOpen: addDigitalAddressOpen, toggle: toggleAddDigitalAddress} = useToggle()
    const {checkGroup: digitalAddressCheckGroup} = useCheckGroup<number>()
    const {isAdmin, isCoordinator,isFrenchTranslator} = useAuth();
    const [setPrimaryAddress, {loading: loadingPrimary}] = useMutation(SET_INDIVIDUAL_PRIMARY_EMAIL)
    const [getIndividualEmails, {loading, data}] = useLazyQuery(GET_INDIVIDUAL_EMAILS, {
        onCompleted: (data) => {
            if (data?.individual?.digitalAddresses) {
                //digitalAddressCheckGroup.init(data.individual.digitalAddresses.map((e: IDigitalAddress) => e.id))
                initCheckGroup(data.individual.digitalAddresses)
                initGropCheckShow(data.individual.digitalAddresses)
            }
        }
    })

    const initCheckGroup = (digitalAddresses:IDigitalAddress[])=>{
        if(digitalAddresses?.length==1)
            digitalAddressCheckGroup.init(digitalAddresses.map((e: IDigitalAddress) => e.id))
        else if(digitalAddresses?.length>1)
            digitalAddressCheckGroup.init(digitalAddresses.filter(digitalAddress=>checkboxLblShow(digitalAddress)).map((e: IDigitalAddress) => e.id))
    }

    const initGropCheckShow = (digitalAddresses:IDigitalAddress[]) => {
        if(digitalAddresses.length===0) 
            setGroupCheckShow(false)
        else if(digitalAddresses.length===1){
            if(isAdmin)
                setGroupCheckShow(true)
            else if(isCoordinator)
                setGroupCheckShow(false)
        }else
            setGroupCheckShow(true)
       
    }

    const [delIndividualEmails, {loading: deIndividualEmailsLoading}] = useMutation(DELETE_INDIVIDUAL_EMAILS);

    useEffect(() => {

        if (individualId) {
            getIndividualEmails({variables: {individualId: individualId}})
        }
    }, [individualId])

    const editDigitalAddress = (digitalAddress: IDigitalAddress) => {
        confirm({
            title: `Edit Individual Email Addresses?`,
            content: `Updates to an individual's email will apply to ALL the positions/roles occupied by this individual, except where a position/role email exists, the position/role email will be published. <br/>Are you sure you want to continue?`,
            okText: <><CheckIcon/> Confirm </>,
            okBtnColor: 'primary'
        }).then(() => {
            setDigitalAddress(digitalAddress)
            toggleEditDigitalAddress()
        })
        
    }

    const setPrimary = (digitalAddress: IDigitalAddress) => {
        setPrimaryAddress({
            variables: {
                individualId: individualId,
                digitalAddressId: digitalAddress.id
            },
            refetchQueries: [{
                query: GET_INDIVIDUAL_EMAILS,
                variables: { individualId: digitalAddress?.businessEntityId }
            }],
            awaitRefetchQueries: true,
            onCompleted: (data) => data && notifySuccess()
        })
    }

    const getAddressTxt = (digitalAddress: IDigitalAddress) => {

        return digitalAddress.digitalType.code === "EMAIL"
            ? digitalAddress.emailAddressTxt
            : (digitalAddress.digitalType.code === "ENINTER" || digitalAddress.digitalType.code === "ENINTRA")
                ? digitalAddress.netAddressTxt
                : "No Address"
    }

    const onAddEmailHandle = () => {
        confirm({
            title: `Add Individual Email Addresses?`,
            content: `Updates to an individual's email will apply to ALL the positions/roles occupied by this individual, except where a position/role email exists, the position/role email will be published. <br/>Are you sure you want to continue?`,
            okText: <><CheckIcon/> Confirm </>,
            okBtnColor: 'primary'
        }).then(() => {
            toggleAddDigitalAddress();
        })
        
    }

    const onRemoveEmailHandle = () => {
        confirm({
            title: `Remove Individual Email Addresses?`,
            content: `The selected Individual email addresses will be removed. <br/>Updates to an individual's email will apply to ALL the positions/roles occupied by this individual, except where a position/role email exists, the position/role email will be published. <br/>Are you sure you want to continue?`,
            okText: <><DeleteIcon/> Remove</>,
            okBtnColor: 'danger'
        }).then(() => {
            let ids=[]
            if(digitalAddressCheckGroup.isAllChecked() && digitalAddressCheckGroup.getCheckedValues().length>1 )
                ids=data.individual.digitalAddresses.filter((e: IDigitalAddress) => e.assignType.code!=='PRIMARY').map((e: IDigitalAddress) => e.id)
            if(digitalAddressCheckGroup.isAllChecked() && digitalAddressCheckGroup.getCheckedValues().length===1 )
                ids=digitalAddressCheckGroup.getCheckedValues()
            if(!digitalAddressCheckGroup.isAllChecked())
                ids=data.individual.digitalAddresses.filter((e: IDigitalAddress) => e.assignType.code!=='PRIMARY'&&digitalAddressCheckGroup.getCheckedValues().includes(e.id)).map((e: IDigitalAddress) => e.id)     
            delIndividualEmails({
                variables: {
                    individualId: individualId,
                    digitalAddressIds: ids
                },
                refetchQueries: [{
                    query: GET_INDIVIDUAL_EMAILS,
                    variables: { individualId: individualId }
                }],
                awaitRefetchQueries: true,
                onCompleted: (data) => {
                    data && notifySuccess()
                }
            });
        })
    }

    const checkboxLblShow=(digitalAddress:IDigitalAddress)=>{
        return  (isAdmin &&
                    (
                        (data?.individual?.digitalAddresses?.length>1 && (!digitalAddress.assignType||digitalAddress.assignType?.code !== "PRIMARY"))
                        ||
                        (data?.individual?.digitalAddresses?.length===1 )
                    )
                )
                ||
                (!isAdmin&&isCoordinator&& (!digitalAddress.assignType||digitalAddress.assignType?.code !== "PRIMARY"))

    }

    return (
        <div>{(isAdmin||isCoordinator) &&
            <Button color="primary" style={{ marginRight: '15px' }}
                    onClick={onAddEmailHandle} disabled={orgReadOnly}>
                    <AddIcon/>
                    &nbsp;<span role="region" aria-label="Add">Add</span>
            </Button>}
            {(isAdmin||isCoordinator) &&
            <Button color="danger" style={{ marginRight: '15px' }}
                    disabled={digitalAddressCheckGroup.isNoneChecked()||orgReadOnly}
                    onClick={onRemoveEmailHandle}>
                    <DeleteIcon />
                    &nbsp;<span role="region" aria-label="Remove">Remove</span>
            </Button>}
            {(loading || loadingPrimary || deIndividualEmailsLoading) && <SpinnerOverlay />}
            {data?.individual?.digitalAddresses &&
                <Table striped id="individual_digital_addresses">
                    <thead>
                        <tr>
                            { !isFrenchTranslator && <th>
                                <span className="sr-only">select digital address</span>
                                {data.individual.digitalAddresses.length > 0 && !digitalAddressCheckGroup.isEmpty() &&
                                    <>
                                        <span id="digitalSelectAllLbl" className="sr-only">Select all</span>
                                        { groupCheckShow &&
                                            <Input
                                                type="checkbox"
                                                title="Select all"
                                                aria-labelledby="digitalSelectAllLbl"
                                                checked={digitalAddressCheckGroup.isAllChecked()}
                                                onChange={digitalAddressCheckGroup.onCheckAll}
                                                disabled={orgReadOnly}
                                            />
                                        }
                                    </>
                                }
                            </th>}
                            <th>Email Address</th>
                            <th>Primary</th>
                            {!isFrenchTranslator && <th><div className="float-end">Edit</div></th>}
                        </tr>
                    </thead>
                    <tbody>
                        {
                            data?.individual?.digitalAddresses.length === 0
                            ?
                            <tr><td colSpan={6} style={{textAlign: 'center'}}>No Individual email addresses found.</td></tr>
                            :
                            data.individual.digitalAddresses.map((digitalAddress: IDigitalAddress, index: number) => {
                                return (
                                    <tr key={index}>
                                        { !isFrenchTranslator && <td>
                                            {checkboxLblShow(digitalAddress)  &&
                                            <Input type="checkbox"
                                                aria-label={'Select digital address ' + getAddressTxt(digitalAddress)}
                                                checked={digitalAddressCheckGroup.isChecked(digitalAddress.id)}
                                                onChange={event => digitalAddressCheckGroup.onCheck(event, digitalAddress.id)}
                                                disabled={orgReadOnly}
                                            />
                                            }
                                        </td>}
                                        <td>{getAddressTxt(digitalAddress)}</td>
                                        <td>
                                            {(digitalAddress.assignType?.code === "PRIMARY" ) || (data.individual.digitalAddresses.length === 1)
                                                ? <CheckMarkIcon></CheckMarkIcon>
                                                : (isAdmin||isCoordinator) &&
                                                    <Input type="checkbox"
                                                    aria-label={`Set primary ${digitalAddress.digitalType.label}`}
                                                    checked={digitalAddress.assignType?.code === "PRIMARY"}
                                                    onChange={()=>setPrimary(digitalAddress)}
                                                    disabled={orgReadOnly}/>
                                            }
                                        </td>

                                        {!isFrenchTranslator && <td>

                                            <ImgButton
                                                className="float-end" aria-label="Edit email" title="Edit email"
                                                disabled={orgReadOnly}
                                                onClick={()=>editDigitalAddress(digitalAddress)}>
                                                <EditIcon className="edit-pencil" />
                                            </ImgButton>

                                        </td>}

                                    </tr>
                                )
                            })
                        }
                    </tbody>
                </Table>
            }

            <IndividualDigitalAddressUpdate digitalAddress={digitalAddress} isOpen={editDigitalAddressOpen} toggle={toggleEditDigitalAddress}/>
            <IndividualDigitalAddressCreate individualId={individualId} isOpen={addDigitalAddressOpen} toggle={toggleAddDigitalAddress}/>

        </div>
    )
}

export default IndividualDigitalAddress;
